import React, { useEffect, useMemo, useState } from "react";
import { ShoppingBag, X, Plus, Minus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Sticky food & drinks ordering bar for the GCR Theaters lobby (/screens).
 * Renders a bottom sticky CTA → tap opens a side panel with the curated menu,
 * cart, and Stripe checkout. Each item is tagged with its provider (Zomato /
 * Noon Food / Paanidost / Cinema Kitchen).
 */
const PROVIDER_PILL = {
  zomato: { label: "Zomato", className: "bg-red-500/15 text-red-300 border-red-500/40" },
  noon: { label: "Noon Food", className: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40" },
  paanidost: { label: "Paanidost", className: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  kitchen: { label: "Cinema Kitchen", className: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
};

export default function SnacksBar({ screenNo }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState({});      // { item_id: qty }
  const [seat, setSeat] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/cinema/snacks")
      .then((r) => setItems(r.data?.snacks || []))
      .catch(() => setItems([]));
  }, []);

  const cartLines = useMemo(() =>
    Object.entries(cart).map(([id, qty]) => ({ id, qty, item: items.find((i) => i.item_id === id) })).filter((l) => l.item),
    [cart, items]);
  const cartTotal = useMemo(() =>
    cartLines.reduce((s, l) => s + l.item.price * l.qty, 0).toFixed(2),
    [cartLines]);
  const cartCount = useMemo(() =>
    Object.values(cart).reduce((s, q) => s + q, 0),
    [cart]);

  const inc = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) => setCart((c) => {
    const q = (c[id] || 0) - 1;
    const out = { ...c };
    if (q <= 0) delete out[id]; else out[id] = q;
    return out;
  });

  const checkout = async () => {
    if (cartLines.length === 0) { toast.error("Cart is empty"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/cinema/snacks/checkout", {
        items: cartLines.map((l) => ({ item_id: l.id, qty: l.qty })),
        screen_no: screenNo || null,
        seat: seat || null,
        notes: notes || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setBusy(false);
    }
  };

  if (items.length === 0) return null;  // no menu configured

  return (
    <>
      {/* Sticky bottom CTA bar */}
      <div data-testid="snacks-bar" className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 shadow-2xl border-t border-amber-300/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-2xl">🍿</span>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-amber-900 font-black leading-none">
                Order food & drinks
              </div>
              <div className="text-sm sm:text-base font-bold text-black leading-tight truncate">
                Popcorn, drinks, snacks — delivered to your seat
              </div>
            </div>
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="snacks-open-cart"
            className="inline-flex items-center gap-2 bg-black text-amber-300 hover:bg-zinc-900 px-4 sm:px-6 py-2.5 rounded-md font-black text-sm shadow-lg transition-colors"
          >
            <ShoppingBag className="w-4 h-4" />
            {cartCount > 0 ? `View cart (${cartCount}) · $${cartTotal}` : "Browse menu"}
          </button>
        </div>
      </div>

      {/* Side panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex" data-testid="snacks-panel">
          <button className="flex-1 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} aria-label="Close" />
          <aside className="w-full max-w-md bg-[#0a0a0a] border-l border-white/10 overflow-y-auto shadow-2xl">
            <header className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 px-5 py-4 flex items-center justify-between">
              <h2 className="text-lg font-black flex items-center gap-2"><span>🍿</span> Cinema Snacks</h2>
              <button onClick={() => setOpen(false)} data-testid="snacks-close" className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 inline-flex items-center justify-center"><X className="w-4 h-4" /></button>
            </header>

            <div className="p-5 space-y-3">
              {items.map((it) => {
                const qty = cart[it.item_id] || 0;
                const pill = PROVIDER_PILL[it.provider] || PROVIDER_PILL.kitchen;
                return (
                  <div key={it.item_id} data-testid={`snack-item-${it.item_id}`} className="bg-[#141414] border border-white/5 rounded-lg p-3 flex items-center gap-3">
                    {it.image_url && <img src={it.image_url} alt={it.name} className="w-16 h-16 object-cover rounded" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{it.name}</div>
                      <div className="text-xs text-gray-400">${it.price.toFixed(2)}</div>
                      <span className={`inline-block mt-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${pill.className}`}>
                        via {pill.label}
                      </span>
                    </div>
                    {qty === 0 ? (
                      <button
                        onClick={() => inc(it.item_id)}
                        data-testid={`snack-add-${it.item_id}`}
                        className="bg-amber-500 hover:bg-amber-400 text-black px-3 py-1.5 rounded text-xs font-black"
                      >
                        + Add
                      </button>
                    ) : (
                      <div className="flex items-center gap-1.5 bg-amber-500/15 border border-amber-500/40 rounded">
                        <button onClick={() => dec(it.item_id)} className="px-2 py-1 text-amber-300"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="text-sm font-bold text-amber-200 min-w-[1ch] text-center">{qty}</span>
                        <button onClick={() => inc(it.item_id)} className="px-2 py-1 text-amber-300"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Order details + checkout */}
            {cartLines.length > 0 && (
              <div className="border-t border-white/10 p-5 space-y-3 bg-[#0f0f0f] sticky bottom-0">
                <input
                  value={seat}
                  onChange={(e) => setSeat(e.target.value)}
                  placeholder="Seat (e.g. C-12)"
                  className="w-full bg-black/40 border border-white/15 rounded px-3 py-2 text-sm"
                  data-testid="snacks-seat"
                />
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes (allergies, no ice, etc.)"
                  rows={2}
                  className="w-full bg-black/40 border border-white/15 rounded px-3 py-2 text-sm resize-none"
                />
                <button
                  onClick={checkout}
                  disabled={busy}
                  data-testid="snacks-checkout"
                  className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-black font-black py-3 rounded-md inline-flex items-center justify-center gap-2"
                >
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</> : `🎟 Pay $${cartTotal} · Stripe`}
                </button>
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
